/**
 * Consolidado del día (diálogo de la liquidación).
 *
 * Es el «Consolidado» del panel anterior (`generarConsolidadoModerno`), que se
 * calculaba en el navegador con los turnos ya cargados: una fila por turno con su
 * cuadre, la **productividad por combustible** (volumen y monto por producto,
 * según las transacciones de cada turno), los totales por serie y los totales del
 * día.
 */

import { Dialog } from '@/components/ui/dialog';
import {
  cn,
  formatCurrency,
  formatNumber,
  formatPaymentMethodName,
} from '@/lib/utils';

import type { TurnoLiquidacion } from '../reportes.api';

interface Props {
  turnos: TurnoLiquidacion[];
  fecha: string;
  abierto: boolean;
  onClose: () => void;
}

interface Producto {
  nombre: string;
  volumen: number;
  monto: number;
  transacciones: number;
}

export function TurnoConsolidadoDialog({ turnos, fecha, abierto, onClose }: Props) {
  /** Productividad por producto a partir de las transacciones de los turnos. */
  const productos = new Map<string, Producto>();

  for (const turno of turnos) {
    // El turno abierto puede venir sin transacciones guardadas (`null`).
    for (const transaccion of turno.transactions ?? []) {
      const nombre = transaccion.FuelGradeName ?? 'Sin producto';
      const registro = productos.get(nombre) ?? { nombre, volumen: 0, monto: 0, transacciones: 0 };

      registro.volumen += Number(transaccion.volumeTransaction ?? 0);
      registro.monto += Number(transaccion.amountTransaction ?? 0);
      registro.transacciones += 1;
      productos.set(nombre, registro);
    }
  }

  const listaProductos = [...productos.values()].sort((a, b) => b.monto - a.monto);

  const metodosNoEfectivo = new Map<string, { metodo: string; monto: number }>();
  for (const turno of turnos) {
    for (const metodo of turno.resumen_financiero?.metodos_pago_no_efectivo ?? []) {
      const clave = metodo.metodo.trim().toLocaleLowerCase('es-PE');
      const registro = metodosNoEfectivo.get(clave) ?? {
        metodo: formatPaymentMethodName(metodo.metodo),
        monto: 0,
      };
      registro.monto += metodo.monto;
      metodosNoEfectivo.set(clave, registro);
    }
  }
  const listaMetodosNoEfectivo = [...metodosNoEfectivo.values()].sort((a, b) => b.monto - a.monto);

  const suma = (obtener: (turno: TurnoLiquidacion) => number) =>
    turnos.reduce((total, turno) => total + obtener(turno), 0);

  const totales = {
    ventas: suma((turno) => Number(turno.resumen_financiero?.total_ventas_bruto ?? 0)),
    lubricantes: suma((turno) => Number(turno.resumen_financiero?.total_lubricantes ?? 0)),
    bovedas: suma((turno) => Number(turno.resumen_financiero?.total_bovedas ?? 0)),
    gastos: suma((turno) => Number(turno.resumen_financiero?.total_gastos ?? 0)),
    descuentosVentasDirectas: suma(
      (turno) => Number(turno.resumen_financiero?.total_descuentos_sin_creditos ?? 0),
    ),
    descuentosCreditos: suma(
      (turno) => Number(turno.resumen_financiero?.total_descuentos_creditos ?? 0),
    ),
    creditos: suma((turno) => Number(turno.resumen_financiero?.total_creditos ?? 0)),
    efectivo: suma((turno) => Number(turno.resumen_financiero?.efectivo_calculado ?? 0)),
    pendiente: suma((turno) => Number(turno.resumen_financiero?.efectivo_pendiente ?? 0)),
    transacciones: suma((turno) => Number(turno.transacciones_series?.total_transacciones ?? 0)),
    volumen: suma((turno) => Number(turno.transacciones_series?.total_general_volumen ?? 0)),
    gnv: suma((turno) => Number(turno.monto_gnv ?? 0)),
  };

  const series = new Map<string, { codigo: string; nombre: string; cantidad: number; monto: number }>();

  for (const turno of turnos) {
    for (const serie of turno.resumen_financiero?.desglose_series ?? []) {
      if (serie.cantidad === 0) continue;

      const registro = series.get(serie.codigo) ?? {
        codigo: serie.codigo,
        nombre: serie.nombre,
        cantidad: 0,
        monto: 0,
      };

      registro.cantidad += serie.cantidad;
      registro.monto += serie.monto;
      series.set(serie.codigo, registro);
    }
  }

  return (
    <Dialog
      open={abierto}
      onClose={onClose}
      title="Consolidado del día"
      description={`${turnos.length} turno(s) del ${fecha} con su cuadre y la productividad por producto.`}
      size="xl"
    >
      <div className="space-y-5">
        <section>
          <h3 className="pb-1 text-sm font-semibold text-ink-800">Turnos</h3>
          <div className="overflow-x-auto">
            <table className="w-full border-collapse text-[13px]">
              <thead>
                <tr className="text-left text-[11px] font-semibold tracking-wide text-ink-500 uppercase">
                  <th className="px-2 py-1.5">Turno</th>
                  <th className="px-2 py-1.5">Tipo</th>
                  <th className="px-2 py-1.5 text-right">Trans.</th>
                  <th className="px-2 py-1.5 text-right">Ventas</th>
                  <th className="px-2 py-1.5 text-right">Bóvedas</th>
                  <th className="px-2 py-1.5 text-right">Gastos</th>
                  <th className="px-2 py-1.5 text-right">Cobros no efectivo</th>
                  <th className="px-2 py-1.5 text-right">Efectivo</th>
                  <th className="px-2 py-1.5 text-right">Pendiente</th>
                </tr>
              </thead>
              <tbody>
                {turnos.map((turno) => {
                  const resumen = turno.resumen_financiero;
                  const pendiente = Number(resumen?.efectivo_pendiente ?? 0);

                  return (
                    <tr key={turno.id} className="border-t border-ink-100">
                      <td className="truncate px-2 py-1.5 text-ink-800">
                        #{turno.id} {turno.first_name ?? ''} {turno.last_name ?? ''}
                      </td>
                      <td className="px-2 py-1.5 text-ink-600">
                        {turno.tipo_turno?.nombre ?? '—'}
                      </td>
                      <td className="px-2 py-1.5 text-right text-ink-600">
                        {formatNumber(resumen?.estadisticas.total_transacciones ?? 0)}
                      </td>
                      <td className="px-2 py-1.5 text-right font-semibold text-ink-900">
                        {formatCurrency(resumen?.total_ventas_bruto ?? 0)}
                      </td>
                      <td className="px-2 py-1.5 text-right text-ink-600">
                        {formatCurrency(resumen?.total_bovedas ?? 0)}
                      </td>
                      <td className="px-2 py-1.5 text-right text-ink-600">
                        {formatCurrency(resumen?.total_gastos ?? 0)}
                      </td>
                      <td className="px-2 py-1.5 text-right text-ink-600">
                        {resumen?.metodos_pago_no_efectivo.map((metodo) => (
                          <div key={metodo.metodo}>
                            {formatPaymentMethodName(metodo.metodo)} · {formatCurrency(metodo.monto)}
                          </div>
                        ))}
                      </td>
                      <td className="px-2 py-1.5 text-right text-ink-700">
                        {formatCurrency(resumen?.efectivo_calculado ?? 0)}
                      </td>
                      <td
                        className={cn(
                          'px-2 py-1.5 text-right font-semibold',
                          Math.abs(pendiente) < 0.005
                            ? 'text-ink-500'
                            : pendiente > 0
                              ? 'text-rose-600'
                              : 'text-amber-700',
                        )}
                      >
                        {formatCurrency(pendiente)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr className="border-t border-ink-100 text-[13px] font-semibold text-ink-900">
                  <td className="px-2 py-1.5" colSpan={2}>
                    TOTAL DEL DÍA
                  </td>
                  <td className="px-2 py-1.5 text-right">{formatNumber(totales.transacciones)}</td>
                  <td className="px-2 py-1.5 text-right">{formatCurrency(totales.ventas)}</td>
                  <td className="px-2 py-1.5 text-right">{formatCurrency(totales.bovedas)}</td>
                  <td className="px-2 py-1.5 text-right">{formatCurrency(totales.gastos)}</td>
                  <td className="px-2 py-1.5 text-right">
                    {listaMetodosNoEfectivo.map((metodo) => (
                      <div key={metodo.metodo}>
                        {metodo.metodo} · {formatCurrency(metodo.monto)}
                      </div>
                    ))}
                  </td>
                  <td className="px-2 py-1.5 text-right">{formatCurrency(totales.efectivo)}</td>
                  <td className="px-2 py-1.5 text-right">{formatCurrency(totales.pendiente)}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        </section>

        <section className="grid gap-5 lg:grid-cols-2">
          <div>
            <h3 className="pb-1 text-sm font-semibold text-ink-800">
              Productividad por producto ({formatNumber(totales.volumen)} de volumen)
            </h3>
            <table className="w-full border-collapse text-[13px]">
              <thead>
                <tr className="text-left text-[11px] font-semibold tracking-wide text-ink-500 uppercase">
                  <th className="px-2 py-1.5">Producto</th>
                  <th className="px-2 py-1.5 text-right">Trans.</th>
                  <th className="px-2 py-1.5 text-right">Volumen</th>
                  <th className="px-2 py-1.5 text-right">Monto</th>
                  <th className="px-2 py-1.5 text-right">Precio medio</th>
                </tr>
              </thead>
              <tbody>
                {listaProductos.length === 0 ? (
                  <tr className="border-t border-ink-100">
                    <td colSpan={5} className="px-2 py-4 text-center text-sm text-ink-500">
                      El día no tiene transacciones.
                    </td>
                  </tr>
                ) : null}

                {listaProductos.map((producto) => (
                  <tr key={producto.nombre} className="border-t border-ink-100">
                    <td className="px-2 py-1.5 text-ink-800">{producto.nombre}</td>
                    <td className="px-2 py-1.5 text-right text-ink-600">
                      {formatNumber(producto.transacciones)}
                    </td>
                    <td className="px-2 py-1.5 text-right text-ink-600">
                      {formatNumber(producto.volumen)}
                    </td>
                    <td className="px-2 py-1.5 text-right font-semibold text-ink-900">
                      {formatCurrency(producto.monto)}
                    </td>
                    <td className="px-2 py-1.5 text-right text-ink-500">
                      {producto.volumen > 0
                        ? formatNumber(producto.monto / producto.volumen)
                        : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="border-t border-ink-100 text-[13px] font-semibold text-ink-800">
                  <td className="px-2 py-1.5">Total</td>
                  <td className="px-2 py-1.5 text-right">{formatNumber(totales.transacciones)}</td>
                  <td className="px-2 py-1.5 text-right">{formatNumber(totales.volumen)}</td>
                  <td className="px-2 py-1.5 text-right">{formatCurrency(totales.ventas)}</td>
                  <td className="px-2 py-1.5" />
                </tr>
              </tfoot>
            </table>
          </div>

          <div className="space-y-4">
            <div>
              <h3 className="pb-1 text-sm font-semibold text-ink-800">Totales por serie</h3>
              <table className="w-full border-collapse text-[13px]">
                <thead>
                  <tr className="text-left text-[11px] font-semibold tracking-wide text-ink-500 uppercase">
                    <th className="px-2 py-1.5">Serie</th>
                    <th className="px-2 py-1.5 text-right">Trans.</th>
                    <th className="px-2 py-1.5 text-right">Monto</th>
                    <th className="px-2 py-1.5 text-right">%</th>
                  </tr>
                </thead>
                <tbody>
                  {[...series.values()]
                    .sort((a, b) => b.monto - a.monto)
                    .map((serie) => (
                      <tr key={serie.codigo} className="border-t border-ink-100">
                        <td className="px-2 py-1.5 text-ink-800">
                          {serie.nombre} ({serie.codigo})
                        </td>
                        <td className="px-2 py-1.5 text-right text-ink-600">
                          {formatNumber(serie.cantidad)}
                        </td>
                        <td className="px-2 py-1.5 text-right font-semibold text-ink-900">
                          {formatCurrency(serie.monto)}
                        </td>
                        <td className="px-2 py-1.5 text-right text-ink-500">
                          {totales.ventas > 0
                            ? formatNumber((serie.monto / totales.ventas) * 100)
                            : '0'}{' '}
                          %
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>

            <div className="grid grid-cols-2 gap-x-4 gap-y-1 rounded-card bg-ink-50/60 p-3 text-[13px] text-ink-600">
              <span>Lubricantes y extras</span>
              <span className="text-right font-semibold text-ink-900">
                {formatCurrency(totales.lubricantes)}
              </span>
              <span>GNV</span>
              <span className="text-right font-semibold text-ink-900">
                {formatCurrency(totales.gnv)}
              </span>
              <span>Descuentos ventas directas</span>
              <span className="text-right font-semibold text-ink-900">
                {formatCurrency(totales.descuentosVentasDirectas)}
              </span>
              <span>Descuentos créditos</span>
              <span className="text-right font-semibold text-ink-900">
                {formatCurrency(totales.descuentosCreditos)}
              </span>
              <span>Vales de crédito</span>
              <span className="text-right font-semibold text-ink-900">
                {formatCurrency(totales.creditos)}
              </span>
              <span className="font-semibold text-ink-800">Faltante / sobrante</span>
              <span className="text-right font-bold text-ink-900">
                {formatCurrency(totales.pendiente)}
              </span>
            </div>
          </div>
        </section>
      </div>
    </Dialog>
  );
}
